/**
 * @type Feature (Lint Stabilization)
 * @rationale Replace 'any' in errorForwarding.ts to satisfy Pass 3 of the stabilization plan.
 */
import { sanitizeData, sanitizeText, sanitizeUrl } from './sanitize';

interface ErrorPayload {
  message: string;
  stack?: string;
  file?: string;
  line?: number;
  column?: number;
  componentStack?: string;
  userActions: UserAction[];
  timestamp: string;
  url: string;
  userAgent: string;
  errorType: 'console' | 'runtime' | 'promise' | 'component' | 'network';
  // Network error specific fields
  httpMethod?: string;
  requestUrl?: string;
  statusCode?: number;
  requestData?: unknown;
  responseData?: unknown;
  errorCode?: string;
  requestId?: string;
}

interface UserAction {
  type: 'click' | 'navigation' | 'input' | 'scroll';
  target?: string;
  timestamp: string;
  value?: string;
}

class ErrorForwardingService {
  private userActions: UserAction[] = [];
  private maxActions = 5;

  private lastErrorTime = new Map<string, number>();
  private debounceMs = 1000;
  private backendUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/console-error`;
  private originalConsoleError: typeof console.error;

  constructor() {
    this.originalConsoleError = console.error.bind(console);

    // Only enable in development
    if (import.meta.env.DEV) {
      this.init();
    }
  }

  private init() {
    this.setupConsoleOverride();
    this.setupWindowErrorHandler();
    this.setupUnhandledRejectionHandler();
    this.setupUserActionTracking();
  }

  private setupConsoleOverride() {
    console.error = (...args: unknown[]) => {
      // Call original first
      this.originalConsoleError(...args);

      // Forward to backend. Object args get structured (key-based) redaction
      // BEFORE stringification — console.error(tokenResponse) is the realistic
      // way a credential object ends up in a message string.
      const message = args.map(arg =>
        arg !== null && typeof arg === 'object'
          ? JSON.stringify(sanitizeData(arg), null, 2)
          : String(arg)
      ).join(' ');

      const error = new Error(message);
      this.forwardError({
        message,
        stack: error.stack,
        errorType: 'console',
      });
    };
  }

  private setupWindowErrorHandler() {
    window.onerror = (message, source, lineno, colno, error) => {
      this.forwardError({
        message: String(message),
        stack: error?.stack,
        file: source,
        line: lineno,
        column: colno,
        errorType: 'runtime',
      });
      return false; // Let default handler run
    };
  }

  private setupUnhandledRejectionHandler() {
    window.onunhandledrejection = (event) => {
      const error = event.reason;
      this.forwardError({
        message: error?.message || String(error),
        stack: error?.stack,
        errorType: 'promise',
      });
    };
  }

  private setupUserActionTracking() {
    // Track clicks
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const selector = this.getElementSelector(target);
      this.addUserAction({
        type: 'click',
        target: selector,
        timestamp: new Date().toISOString(),
      });
    }, true);

    // Track navigation
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      this.addUserAction({
        type: 'navigation',
        value: args[2]?.toString(),
        timestamp: new Date().toISOString(),
      });
      return originalPushState.apply(history, args);
    };

    history.replaceState = (...args) => {
      this.addUserAction({
        type: 'navigation',
        value: args[2]?.toString(),
        timestamp: new Date().toISOString(),
      });
      return originalReplaceState.apply(history, args);
    };

    // Track inputs (debounced)
    let inputTimeout: number;
    document.addEventListener('input', (e) => {
      clearTimeout(inputTimeout);
      inputTimeout = window.setTimeout(() => {
        const target = e.target as HTMLInputElement;
        if (target.type !== 'password') {
          this.addUserAction({
            type: 'input',
            target: this.getElementSelector(target),
            timestamp: new Date().toISOString(),
          });
        }
      }, 500);
    }, true);
  }

  private getElementSelector(element: HTMLElement): string {
    if (element.id) return `#${element.id}`;
    
    // Safely handle SVG elements where className is an object (SVGAnimatedString)
    const className = typeof element.className === 'string' 
      ? element.className 
      : (element.getAttribute && element.getAttribute('class')) || '';
      
    if (className) {
      const classes = className.split(' ').filter(c => c).slice(0, 2).join('.');
      return `${element.tagName.toLowerCase()}.${classes}`;
    }
    return element.tagName.toLowerCase();
  }

  private addUserAction(action: UserAction) {
    this.userActions.push(action);
    if (this.userActions.length > this.maxActions) {
      this.userActions.shift();
    }
  }

  public forwardError(errorData: Partial<ErrorPayload>) {
    const errorKey = `${errorData.message}:${errorData.file}:${errorData.line}`;

    // Check debounce
    const lastTime = this.lastErrorTime.get(errorKey);
    const now = Date.now();
    if (lastTime && now - lastTime < this.debounceMs) {
      return; // Skip duplicate
    }

    this.lastErrorTime.set(errorKey, now);

    const payload: ErrorPayload = {
      // Free-text fields can embed secrets (a thrown message with a token, a
      // stack frame URL); scrub unmistakable secret shapes at the choke point.
      message: sanitizeText(errorData.message) ?? 'Unknown error',
      stack: sanitizeText(errorData.stack),
      file: sanitizeUrl(errorData.file),
      line: errorData.line,
      column: errorData.column,
      componentStack: sanitizeText(errorData.componentStack),
      // Navigation actions record the destination URL, which can carry a
      // bearer token in its path or query (e.g. the public /a/<token> form) —
      // redact it like every other URL field.
      userActions: this.userActions.map((action) =>
        action.type === 'navigation' && action.value
          ? { ...action, value: sanitizeUrl(action.value) ?? action.value }
          : action,
      ),
      timestamp: new Date().toISOString(),
      // The page URL can carry secrets in its query/fragment — e.g. an MSAL
      // auth-code redirect leaves `…/redirect?code=<authcode>` in the location
      // bar, and any error forwarded while on that page would ship the code.
      // Redact query values + fragment at this single choke point.
      url: sanitizeUrl(window.location.href) ?? window.location.href,
      userAgent: navigator.userAgent,
      errorType: errorData.errorType || 'runtime',
      // Network-specific fields. These were previously dropped here (the payload
      // did not copy them), so httpMethod/statusCode/requestData/etc. and the
      // backend request id never reached the server. Forward them now.
      httpMethod: errorData.httpMethod,
      // Same choke-point URL redaction for the request URL, so no caller (axios
      // or fetch interceptor, or any future one) can leak `?access_token=…`.
      requestUrl: sanitizeUrl(errorData.requestUrl),
      statusCode: errorData.statusCode,
      // Final choke point: deep-redact regardless of which caller (axios or fetch
      // interceptor, or any future one) produced the data, so credentials can
      // never reach the telemetry endpoint.
      requestData: sanitizeData(errorData.requestData),
      responseData: sanitizeData(errorData.responseData),
      errorCode: errorData.errorCode,
      requestId: errorData.requestId,
    };

    // Send to backend
    fetch(this.backendUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).catch(err => {
      // Silently fail - don't create error loop
      this.originalConsoleError('Failed to forward error:', err);
    });
  }

  // Public method for React Error Boundary
  public reportComponentError(error: Error, componentStack: string) {
    this.forwardError({
      message: error.message,
      stack: error.stack,
      componentStack,
      errorType: 'component',
    });
  }

  // Public method for network errors (axios, fetch)
  public reportNetworkError(errorData: {
    message: string;
    httpMethod?: string;
    requestUrl?: string;
    statusCode?: number;
    requestData?: unknown;
    responseData?: unknown;
    errorCode?: string;
    stack?: string;
    requestId?: string;
  }) {
    this.forwardError({
      ...errorData,
      errorType: 'network',
    });
  }
}

// Singleton instance
export const errorForwardingService = new ErrorForwardingService();
